/**
 * AI Service for OpenSCAD generation via OpenRouter
 */

import { OpenRouter } from '@openrouter/sdk';
import { OpenRouterModel, AiTokenUsage } from '@/types/ai';

/**
 * Create an OpenRouter client with the user's API key
 */
export function createAiClient(apiKey: string): OpenRouter {
  return new OpenRouter({
    apiKey,
  });
}

/**
 * Fetch available models from OpenRouter
 */
export async function fetchModels(
  client: OpenRouter,
): Promise<OpenRouterModel[]> {
  try {
    const response = await client.models.list();

    // Filter for text generation models and map to our type
    const models = (response.data || [])
      .filter((model) => {
        // Filter out image-only models
        const modality = model.architecture?.modality || '';
        return modality.includes('text') || !modality;
      })
      .map((model) => ({
        id: model.id,
        name: model.name || model.id,
        description: model.description,
        pricing: {
          prompt: model.pricing?.prompt || '0',
          completion: model.pricing?.completion || '0',
        },
        context_length: model.contextLength || 4096,
        top_provider: model.topProvider?.maxCompletionTokens
          ? { max_completion_tokens: model.topProvider.maxCompletionTokens }
          : undefined,
        architecture: model.architecture
          ? {
              modality: model.architecture.modality || undefined,
              tokenizer: model.architecture.tokenizer || undefined,
            }
          : undefined,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return models;
  } catch (error) {
    console.error('Failed to fetch models:', error);
    throw error;
  }
}

/**
 * Calculate cost from token usage and model pricing
 */
export function calculateCost(
  model: OpenRouterModel,
  promptTokens: number,
  completionTokens: number,
): number {
  const promptCost = parseFloat(model.pricing.prompt) * promptTokens;
  const completionCost =
    parseFloat(model.pricing.completion) * completionTokens;
  return promptCost + completionCost;
}

/**
 * Aggregate token usage from multiple steps
 */
export function aggregateTokenUsage(
  usages: Array<{ prompt: number; completion: number }>,
): Omit<AiTokenUsage, 'estimatedCost'> {
  return usages.reduce(
    (acc, usage) => ({
      promptTokens: acc.promptTokens + usage.prompt,
      completionTokens: acc.completionTokens + usage.completion,
      totalTokens: acc.totalTokens + usage.prompt + usage.completion,
    }),
    { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
  );
}

/** A content part for multimodal messages.
 *  Uses camelCase `imageUrl` to match the OpenRouter SDK's input schema
 *  (the SDK transforms to snake_case `image_url` when sending to the API). */
export type ChatContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; imageUrl: { url: string } };

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string | ChatContentPart[];
}

export interface StreamCallbacks {
  onChunk?: (content: string) => void;
  onComplete?: (
    fullContent: string,
    usage: { prompt: number; completion: number },
  ) => void;
  onError?: (error: Error) => void;
}

/**
 * Stream a chat completion from OpenRouter
 * Uses the SDK's chat.send() method with stream: true
 */
export async function streamChatCompletion(
  client: OpenRouter,
  model: string,
  messages: ChatMessage[],
  callbacks: StreamCallbacks,
  abortSignal?: AbortSignal,
): Promise<{ content: string; usage: { prompt: number; completion: number } }> {
  let fullContent = '';
  let usage = { prompt: 0, completion: 0 };

  try {
    // Use the SDK's streaming API
    // Cast messages to satisfy SDK types -- multimodal content arrays are valid for OpenRouter
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mappedMessages = messages.map((m) => ({
      role: m.role,
      content: m.content,
    })) as any[];
    const stream = await client.chat.send({
      model,
      messages: mappedMessages,
      stream: true,
    });

    for await (const chunk of stream) {
      // Check for abort
      if (abortSignal?.aborted) {
        throw new Error('Generation cancelled');
      }

      // Extract content from chunk
      const content = chunk.choices?.[0]?.delta?.content || '';
      if (content) {
        fullContent += content;
        callbacks.onChunk?.(content);
      }

      // Extract usage from chunk if available
      if (chunk.usage) {
        usage = {
          prompt: chunk.usage.promptTokens || 0,
          completion: chunk.usage.completionTokens || 0,
        };
      }
    }

    callbacks.onComplete?.(fullContent, usage);
    return { content: fullContent, usage };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    callbacks.onError?.(err);
    throw err;
  }
}

/**
 * Non-streaming chat completion (for simpler cases)
 * Uses the SDK's chat.send() method with stream: false
 */
export async function chatCompletion(
  client: OpenRouter,
  model: string,
  messages: ChatMessage[],
): Promise<{ content: string; usage: { prompt: number; completion: number } }> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mappedMsgs = messages.map((m) => ({
      role: m.role,
      content: m.content,
    })) as any[];
    const response = await client.chat.send({
      model,
      messages: mappedMsgs,
      stream: false,
    });

    const rawContent = response.choices?.[0]?.message?.content;
    // Handle content that can be string or array
    const content =
      typeof rawContent === 'string'
        ? rawContent
        : Array.isArray(rawContent)
          ? rawContent
              .filter((item) => 'text' in item)
              .map((item) => (item as { text: string }).text)
              .join('')
          : '';
    const usage = {
      prompt: response.usage?.promptTokens || 0,
      completion: response.usage?.completionTokens || 0,
    };

    return { content, usage };
  } catch (error) {
    console.error('Chat completion error:', error);
    throw error;
  }
}

/**
 * Test API key by making a minimal request
 */
export async function testApiKey(apiKey: string): Promise<boolean> {
  try {
    const client = createAiClient(apiKey);
    // Try to fetch models as a lightweight test
    await client.models.list();
    return true;
  } catch (error) {
    console.error('API key test failed:', error);
    return false;
  }
}

/**
 * Get popular/recommended models for the model selector
 */
export function getRecommendedModels(): string[] {
  return [
    'anthropic/claude-sonnet-4',
    'anthropic/claude-3.5-sonnet',
    'openai/gpt-4o',
    'openai/gpt-4o-mini',
    'google/gemini-2.0-flash-001',
    'google/gemini-pro-1.5',
    'deepseek/deepseek-chat',
    'meta-llama/llama-3.1-70b-instruct',
  ];
}

/**
 * Format model price for display
 */
export function formatModelPrice(pricePerToken: string): string {
  const price = parseFloat(pricePerToken);
  if (price === 0) return 'Free';

  // Convert to price per 1M tokens for readability
  const pricePerMillion = price * 1_000_000;

  if (pricePerMillion < 0.01) {
    return `$${pricePerMillion.toFixed(4)}/M`;
  } else if (pricePerMillion < 1) {
    return `$${pricePerMillion.toFixed(3)}/M`;
  } else {
    return `$${pricePerMillion.toFixed(2)}/M`;
  }
}

/**
 * Format cost for display
 */
export function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.0001) return '<$0.0001';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}
